import {
  AGENT_MODEL_OPTIONS,
  DELIVERY_STATUS_BACKLOG,
  PROFILE_TYPE_AI_AGENT,
  TICKET_TYPE_STORY
} from "../../domainCatalog";

export const updateDraft = (setter, field, value) => setter((current) => ({ ...current, [field]: value }));

export const toDraft = (ticket) => ({
  parent_id: ticket.parent_id,
  assignee_id: ticket.assignee_id,
  reviewer_id: ticket.reviewer_id || null,
  ticket_type: ticket.ticket_type,
  title: ticket.title,
  description: ticket.description,
  acceptance_criteria: ticket.acceptance_criteria,
  dependencies: ticket.dependencies,
  assumptions: ticket.assumptions,
  complexity: ticket.complexity,
  status: ticket.status,
  delivery_status: ticket.delivery_status || DELIVERY_STATUS_BACKLOG
});

export const withoutAssignment = ({ parent_id, assignee_id, reviewer_id, ...ticket }) => ticket;

export const assignmentLabel = (assignee, reviewer) => {
  if (!assignee) return "Unassigned";
  if (assignee.profile_type === PROFILE_TYPE_AI_AGENT) return `AI ${assignee.name} · ${reviewer?.name || "Needs reviewer"}`;
  return assignee.name;
};

export const agentModelLabel = (model) => AGENT_MODEL_OPTIONS.find((option) => option.id === model)?.label || AGENT_MODEL_OPTIONS[0].label;

export const currentPublishEvent = (job) => [...(job?.events || [])].reverse().find((event) => event.metadata?.ticket_title);

export const publishedTicketNames = (job) => (job?.events || [])
  .filter((event) => event.message?.includes("published to Jira") && event.metadata?.ticket_title)
  .map((event) => event.metadata.ticket_title);

export const refreshSelected = (tickets, selectedId, setDraft, setSavedDraft) => {
  const selected = tickets.find((ticket) => ticket.id === selectedId);
  if (!selected) return;
  const nextDraft = toDraft(selected);
  setDraft(nextDraft);
  setSavedDraft(nextDraft);
};

export const moveBacklogTicket = (tickets, draggedId, targetId) => {
  const working = tickets.map((ticket) => ({ ...ticket }));
  const dragged = working.find((ticket) => ticket.id === draggedId);
  const target = working.find((ticket) => ticket.id === targetId);
  if (!dragged || !target) return null;
  const stories = working.filter((ticket) => ticket.ticket_type === TICKET_TYPE_STORY);
  const childrenByStory = Object.fromEntries(stories.map((story) => [
    story.id,
    working.filter((ticket) => ticket.parent_id === story.id)
  ]));

  if (dragged.ticket_type === TICKET_TYPE_STORY) {
    if (target.ticket_type !== TICKET_TYPE_STORY) return null;
    moveToTargetPosition(stories, dragged, target);
  } else {
    const source = childrenByStory[dragged.parent_id] || [];
    const sourceParentId = dragged.parent_id;
    const originalTargetIndex = sourceParentId === target.parent_id
      ? source.findIndex((ticket) => ticket.id === target.id)
      : -1;
    source.splice(source.findIndex((ticket) => ticket.id === dragged.id), 1);
    if (target.ticket_type === TICKET_TYPE_STORY) {
      dragged.parent_id = target.id;
      childrenByStory[target.id].push(dragged);
    } else {
      dragged.parent_id = target.parent_id;
      const destination = childrenByStory[target.parent_id] || [];
      const targetIndex = originalTargetIndex >= 0
        ? originalTargetIndex
        : destination.findIndex((ticket) => ticket.id === target.id);
      destination.splice(targetIndex, 0, dragged);
    }
  }

  return stories.flatMap((story, storyPosition) => [
    { ...story, position: storyPosition },
    ...(childrenByStory[story.id] || []).map((ticket, position) => ({ ...ticket, parent_id: story.id, position }))
  ]);
};

const moveToTargetPosition = (items, dragged, target) => {
  const targetIndex = items.findIndex((item) => item.id === target.id);
  items.splice(items.findIndex((item) => item.id === dragged.id), 1);
  items.splice(targetIndex, 0, dragged);
};

export const deliveryStatusClass = (status = DELIVERY_STATUS_BACKLOG) => status.toLowerCase().replaceAll(" ", "-");

export const formatDate = (value) => value || "Not scheduled";

export const jobStatusMessage = (job, label) => {
  if (job.status === "completed") return `${label} completed.`;
  if (job.status === "failed") return job.error_message || `${label} failed.`;
  return `${label}: ${job.current_step || "Queued"} (${job.progress || 0}%).`;
};
